<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="500">
        <v-form v-model="formData.valid" class="mb-3" validate-on="input" @submit.prevent="submitForm">
            <v-card>
                <v-card-title id="heading" class="d-flex justify-space-between">
                    <h4 v-if="otaPackage">Update "{{ otaPackage.label }}" OTA Package</h4>
                    <h4 v-else>Add New OTA Package</h4>
                    <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
                </v-card-title>

                <v-container>
                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                             variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>
                    <v-row>
                        <v-col class="pb-0" cols="12">
                            <v-select v-model="formData['product_sku']" :items="productSkus" :loading="loading"
                                      :rules="[vRules.required]" item-title="sku" item-value="id" label="Product Sku"
                                      @update:modelValue="()=>{ formData['ota_files'] = null; fetchFilesList()}">
                            </v-select>
                        </v-col>
                        <v-col class="pb-0" cols="12">
                            <v-select v-model="formData['ota_files']" :items="otaFiles" :loading="loading"
                                      :rules="[vRules.required]" chips item-title="name" item-value="id"
                                      label="OTA Files"
                                      multiple>
                                <template v-slot:details>
                                    <small class="text-start">Please select files in ASC order in which you want
                                        gateway to install</small>
                                </template>
                            </v-select>
                        </v-col>

                        <v-col class="pb-0" cols="12">
                            <v-text-field v-model="formData['label']" autofocus :rules="[vRules.required]" label="Package Name"/>
                        </v-col>
                        <v-col class="pb-0" cols="12">
                            <v-text-field v-model="formData['version']" :rules="[vRules.required]"
                                          label="Package Version"/>
                        </v-col>
                        <v-col class="pb-0" cols="12">
                            <v-textarea v-model="formData['notes']" label="Notes" rows="2" auto-grow/>
                        </v-col>
                    </v-row>
                </v-container>

                <v-card-actions class="px-4 d-flex justify-end">
                    <v-btn size="small" type="button"
                           @click="$emit('update:modelValue',false)">
                        Close
                    </v-btn>
                    <v-btn :disabled="!formData.valid" color="primary" size="small" type="submit">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script setup>
import {ref, reactive, onMounted} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";

const snackbar = useSnackbarStore();
const props = defineProps({
    modelValue: Boolean,
    otaPackage: Object,
});
const formData = reactive({
    valid: false,
    ota_files: null,
    product_sku: null,
    label: props.otaPackage?.label ?? null,
    version: props.otaPackage?.version ?? null,
    notes: props.otaPackage?.notes ?? null,
});
const emits = defineEmits(['update:modelValue', 'addPackage', 'updatePackage']);
const dialogSwitch = ref(props.modelValue);
const productSkus = ref([]);
const loading = ref(true);
const otaFiles = ref([]);
const errors = ref([]);

onMounted(async () => {
    await fetchProductSkusList().then(async () => {
        if (props.otaPackage) {
            formData['product_sku'] = props.otaPackage?.product_sku_id;
            await fetchFilesList().then(async () => {
                fetchPackageFiles();
            });
        }
    }).finally(() => loading.value = false);
})
const fetchProductSkusList = () => {
    return send('get', `product-skus`, {list: true})
        .then((response) => {
            productSkus.value = response.data.data.product_skus;
        }).catch((error) => {
            snackbar.showSnackbar('Product skus list could not be loaded', 'error')
        })
}
const fetchFilesList = () => {
    return send('get', `admin/ota/files`, {list: true, product_sku: formData['product_sku']})
        .then((response) => {
            otaFiles.value = response.data.data;
        }).catch((error) => {
            snackbar.showSnackbar('OTA Files list could not be loaded', 'error')
        })
}
const fetchPackageFiles = () => {
    return send('get', `admin/ota/package-files/${props.otaPackage.id}`)
        .then((response) => {
            if (response.data.data) {
                formData['ota_files'] = response.data.data.map((otaFile) => otaFile.id);
            }
        }).catch((error) => {
            console.log(error)
            snackbar.showSnackbar('Package Files list could not be loaded', 'error')
        })
}
const submitForm = () => {
    errors.value = [];
    if (props.otaPackage) {
        send('put', `admin/ota/packages/${props.otaPackage.id}`, formData)
            .then((response) => {
                snackbar.showSnackbar(response.data.message);
                if (response.data.success) {
                    emits('updatePackage', response.data.data);
                    emits('update:modelValue', false);
                }
            })
            .catch(function (errorResponse) {
                if (errorResponse.response?.data?.errors) {
                    let validationErrors = errorResponse.response.data.errors;
                    Object.keys(validationErrors).forEach((key) => {
                        errors.value.push(validationErrors[key][0]);
                    });
                } else if (errorResponse.response?.data.message) {
                    errors.value.push(errorResponse.response.data.message);
                }
                snackbar.showSnackbar('Something went wrong', 'error');
                document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
            })
    } else {
        send('post', `admin/ota/packages`, formData)
            .then((response) => {
                snackbar.showSnackbar(response.data.message);
                if (response.data.success) {
                    emits('addPackage', response.data.data);
                    emits('update:modelValue', false);
                }
            })
            .catch(function (errorResponse) {
                console.log(errorResponse);
                if (errorResponse.response?.data.errors) {
                    let validationErrors = errorResponse.response.data.errors;
                    Object.keys(validationErrors).forEach((key) => {
                        errors.value.push(validationErrors[key][0]);
                    });
                } else if (errorResponse.response?.data.message) {
                    errors.value.push(errorResponse.response.data.message);
                }
                snackbar.showSnackbar('Something went wrong', 'error');
                document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
            })
    }
}

</script>
